class ZendeskForm extends HTMLElement {
  constructor() {
    super();
    this.formElement = null;
    this.submitButton = null;
    this.zendeskUrl = this.dataset.zendeskUrl;
    this.basinEndpoint = this.dataset.basinEndpoint;
    this.redirectUrl = this.dataset.redirectUrl;
    this.sendingText = this.dataset.sendingText;
    this.submitText = this.dataset.submitText;
    this.fields = {
      name: null,
      email: null,
      phone: null,
      message: null,
      locale: null
    };
  }

  connectedCallback() {
    this.initializeForm();
    this.attachEventListeners();
  }

  initializeForm() {
    this.formElement = this.querySelector('#zendesk-form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.statusElement = this.querySelector('.form-status');
    this.statusMessage = this.querySelector('.form-status__message');
    this.validatorElement = this.querySelector('wcag-form-validator');

    // Initialize field references
    Object.keys(this.fields).forEach(field => {
      this.fields[field] = this.querySelector(`#zendesk-${field}`);
    });
  }

  attachEventListeners() {
    this.formElement.querySelector('button[type="submit"]').addEventListener('click', (e) => this.handleSubmit(e));
  }

  async handleSubmit(event) {
    event.preventDefault();
    // Client-side validation via shared validator (if available)
    try {
      if (this.validatorElement && typeof this.validatorElement.validate === 'function') {
        this.validatorElement.clear?.();
        const errors = this.validatorElement.validate();
        if (errors && errors.length) {
          this.validatorElement.showErrors?.(errors);
          return;
        }
      }
    } catch (_) {
      // If validator misbehaves, proceed without blocking submission
    }

    this.setLoading(true);

    try {
      if (this.basinEndpoint) {
        const fd = this.getBasinFormData();
        const response = await this.submitToBasin(fd);
        if (!response.ok) throw new Error('Failed to submit');
      } else {
        const formData = this.getFormData();
        const response = await this.submitToZendesk(formData);
        if (!response.ok) throw new Error('Failed to submit ticket');
      }
      
      this.formElement.reset();
      if (this.redirectUrl) {
        window.location.href = this.redirectUrl;
      }
    } catch (error) {
      this.showError(error);
    } finally {
      this.setLoading(false);
    }
  }

  getFormData() {
    return {
      request: {
        requester: {
          name: this.fields.name.value,
          email: this.fields.email.value,
          phone: this.fields.phone.value,
          locale: this.fields.locale.value
        },
        subject: 'Contact Form Submission',
        comment: {
          body: this.fields.message.value
        },
        type: 'question',
        via: {
          channel: 'web_form',
        },
      }
    };
  }

  async submitToZendesk(formData) {
    const requestUrl = 'https://' + this.zendeskUrl + '/api/v2/requests.json';
    return fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
}

  getBasinFormData() {
    const fd = new FormData();
    fd.append('name', this.fields.name.value || '');
    fd.append('email', this.fields.email.value || '');
    fd.append('phone', this.fields.phone.value || '');
    fd.append('message', this.fields.message.value || '');
    fd.append('locale', this.fields.locale.value || '');
    return fd;
  }

  async submitToBasin(formData) {
    return fetch(this.basinEndpoint, {
      method: 'POST',
      body: formData
    });
  }

  setLoading(isLoading) {
    this.submitButton.disabled = isLoading;
    this.submitButton.textContent = isLoading ? this.dataset.sendingText : this.dataset.submitText;
  }

  showSuccess() {
    this.statusElement.hidden = false;
    this.statusElement.classList.add('form-status--success');
    this.statusElement.classList.remove('form-status--error');
    this.statusMessage.textContent = this.successMessage;
  }

  showError(error) {
    this.statusElement.hidden = false;
    this.statusElement.classList.add('form-status--error');
    this.statusElement.classList.remove('form-status--success');
    this.statusMessage.textContent = 'Sorry, there was an error submitting your message. Please try again.';
    console.error('Form submission error:', error);
  }
}

customElements.define('zendesk-form', ZendeskForm);